import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    ChecklistOutlined,
    HomeRepairServiceOutlined,
    MonetizationOnOutlined,
    InfoOutlined,
    CheckCircleOutline,
    ArrowForwardIos,
    Close,
    ArrowRightAlt,
    CleaningServicesOutlined,
    AssignmentTurnedInOutlined,
    WarningAmberOutlined
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェア退去準備のポイント' },
    { id: 'genjokaifuku', label: '原状回復の注意点' },
    { id: 'shikikin', label: '敷金返還のコツ' },
    { id: 'checklist', label: '退去時チェックリスト' },
    { id: 'faq', label: 'よくある質問・トラブル事例' },
    { id: 'summary', label: 'まとめ' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)} sx={{ ml: 2 }}>
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem
                                    key={item.id}
                                    sx={{
                                        p: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 0.5
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 32,
                                            minHeight: 32,
                                            bgcolor: '#f5f5f5',
                                            color: '#666',
                                            fontWeight: 'bold',
                                            borderRadius: 1,
                                            fontSize: '1.15rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1
                                        }}
                                    >
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left', px: 0 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェア退去準備のポイント
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 3 }}>
                    ルームシェア生活を終えるとき、「どんな準備が必要？」「敷金はちゃんと戻ってくる？」と不安になる方は多いのではないでしょうか。<b>退去時の段取り</b>や<b>原状回復</b>、<b>敷金返還</b>のポイントを押さえておくことで、後悔のないスムーズな退去を実現できます。
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={7}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                            この記事では、<b>ルームシェア退去時に必要なチェックポイント</b>を専門的な視点でやさしく解説。<Chip label="原状回復" color="primary" size="small" sx={{ mx: 0.5 }} />や<Chip label="敷金返還" color="primary" size="small" sx={{ mx: 0.5 }} />など重要キーワードも押さえつつ、<b>実践的なチェックリスト</b>もご用意しました。
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            
                        </Box>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    さあ、円満な退去を目指して、今からできる準備を始めましょう！
                </Typography>
            </Box>

            {/* 原状回復の注意点 */}
            <Box id="genjokaifuku" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 1 }}>
                        <HomeRepairServiceOutlined sx={{ mr: 1, color: 'primary.main' }} />
                        原状回復の注意点
                    </Box>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    退去時に最もトラブルが多いのが「原状回復」です。<b>原状回復</b>とは、入居時の状態にお部屋を戻すこと。しかし、<b>どこまで直せばいいのか</b>迷う方も多いでしょう。
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#fafafa', borderLeft: '4px solid #2196f3' }} elevation={0}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        <InfoOutlined sx={{ verticalAlign: 'middle', mr: 0.5, color: 'primary.main' }} />
                        原状回復のルールはココ！
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutline color="success" />
                            </ListItemIcon>
                            <ListItemText
                                primary="故意・過失によるキズや汚れは入居者負担"
                                secondary="家具の移動による大きなキズ、タバコのヤニやペットの汚れなどは修繕費が発生します。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleOutline color="success" />
                            </ListItemIcon>
                            <ListItemText
                                primary="経年劣化や通常使用による消耗は貸主負担"
                                secondary="日焼けによる壁紙の変色や、家具設置による軽度な凹みは原則として請求されません。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <WarningAmberOutlined color="warning" />
                            </ListItemIcon>
                            <ListItemText
                                primary="共有部のキズ・汚れにも注意"
                                secondary="リビングやキッチンなどの共用部も、ルームメイト全員で責任を持って確認しましょう。"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ my: 2 }}>
                    <img
                        src="/images/blog/checkroom-cleaning_roomshare-taikyo-checklist.jpg"
                        alt="退去前にルームメイト複数人で共有スペースのキズや汚れを一緒に点検している場面。明るく協力的な雰囲気"
                        style={{ width: '100%', maxWidth: 440, borderRadius: 12, boxShadow: '0 2px 12px rgba(80,120,220,0.10)' }}
                    />
                </Box>
                <Typography variant="body2" sx={{ mt: 2 }}>
                    <b>POINT:</b> <b>写真で記録</b>を残すことでトラブル防止！<br />
                    壁・床・設備など、入居時・退去時の写真をスマホで撮っておくと安心です。
                </Typography>
            </Box>

            {/* 敷金返還のコツ */}
            <Box id="shikikin" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 1 }}>
                        <MonetizationOnOutlined sx={{ mr: 1, color: 'primary.main' }} />
                        敷金返還のコツ
                    </Box>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    敷金トラブルを避けるためには、<b>事前準備</b>と<b>記録</b>がカギとなります。特にルームシェアの場合、<b>誰がどの部屋を使っていたか</b>、<b>共有部分の責任分担</b>なども明確にしておきましょう。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, mb: 2, bgcolor: '#f1f8e9', borderLeft: '4px solid #43a047' }} elevation={0}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                <AssignmentTurnedInOutlined sx={{ verticalAlign: 'middle', mr: 0.5, color: 'success.main' }} />
                                敷金をしっかり取り戻すためのコツ
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="契約書を再確認" secondary="原状回復や敷金返還条件を事前にチェック。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="退去日までに十分な清掃" secondary="特に水回りやキッチンの汚れは入念に。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="立ち会い時は細部まで確認" secondary="その場で写真を撮り、修繕箇所は明確に記録。" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleOutline color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="返金予定日と金額を書面で確認" secondary="口頭だけでなくメールや書類で残しましょう。" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            
                        </Box>
                    </Grid>
                </Grid>
                <Typography variant="body2" sx={{ mt: 2 }}>
                    <b>豆知識：</b>ルームメイトで退去時期がずれる場合は、<b>敷金の分配方法</b>もルール化しておくとトラブルを防げます！
                </Typography>
            </Box>

            {/* 退去時チェックリスト */}
            <Box id="checklist" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 1 }}>
                        <ChecklistOutlined sx={{ mr: 1, color: 'primary.main' }} />
                        退去時チェックリスト
                    </Box>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    退去直前はやることが多くて忘れがち。<b>このリストを参考に、漏れなく準備を進めましょう！</b>
                </Typography>
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#fffde7', borderLeft: '4px solid #ffb300' }} elevation={0}>
                    <List>
                        <ListItem>
                            <ListItemIcon><CleaningServicesOutlined color="primary" /></ListItemIcon>
                            <ListItemText primary="共有部・専有部ともに掃除" secondary="特に水回りやキッチンは念入りに！" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><AssignmentTurnedInOutlined color="primary" /></ListItemIcon>
                            <ListItemText primary="ゴミ・粗大ごみの分別と処分" secondary="地域のルールに従い、最終回収日に合わせて手配" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><HomeRepairServiceOutlined color="primary" /></ListItemIcon>
                            <ListItemText primary="破損箇所の確認・修繕" secondary="小さな穴やキズもチェック。必要に応じて修理依頼を" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><MonetizationOnOutlined color="primary" /></ListItemIcon>
                            <ListItemText primary="契約書・敷金返還条件の再確認" secondary="同意事項や返金日・金額もチェック" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                            <ListItemText primary="立ち会い日程の調整" secondary="管理会社やルームメイトとスケジュールを共有" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                            <ListItemText primary="郵便物の転送・住所変更手続き" secondary="役所・銀行・通販サイトなども忘れずに" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                            <ListItemText primary="退去後の室内写真を撮影" secondary="トラブル防止のため、各部屋・設備ごとに記録" />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ textAlign: 'right', mt: 1 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<ArrowRightAlt />}
                        href="#"
                        sx={{ textTransform: 'none' }}
                    >
                        PDF版チェックリストをダウンロード
                    </Button>
                </Box>
            </Box>

            {/* よくある質問・トラブル事例 */}
            <Box id="faq" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 1 }}>
                        <InfoOutlined sx={{ mr: 1, color: 'primary.main' }} />
                        よくある質問・トラブル事例
                    </Box>
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ mb: 2, bgcolor: '#f3e5f5', borderLeft: '4px solid #8e24aa' }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                                    敷金が思ったより戻らない…
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <b>よくある原因：</b> 原状回復費用として多めに差し引かれるケース。<br />
                                    <b>対策：</b> 立ち会い時にその場で明細を確認し、納得いかない場合は理由を説明してもらいましょう。
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ bgcolor: '#fff3e0', borderLeft: '4px solid #fb8c00' }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                                    共有部のキズや汚れは誰の責任？
                                </Typography>
                                <Typography variant="body2">
                                    <b>基本：</b> ルームメイト全員の連帯責任。個人利用部分は各自が責任を持つのが原則です。<br />
                                    <b>アドバイス：</b> 入居時・退去時に「共有部の状態」を写真で残し、全員で確認しましょう。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ mb: 2, bgcolor: '#e1f5fe', borderLeft: '4px solid #039be5' }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                                    退去立ち会いに全員参加できない場合は？
                                </Typography>
                                <Typography variant="body2">
                                    代表者が立ち会い、内容を写真やチャットで全員に共有しましょう。後日、同意サインや確認書類の回覧も有効です。
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '4px solid #43a047' }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                                    退去後に追加請求されたら？
                                </Typography>
                                <Typography variant="body2">
                                    写真記録や契約書を元に交渉。納得できなければ消費生活センター等への相談も検討しましょう。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                    退去時は、<b>原状回復</b>・<b>敷金返還</b>・<b>段取り</b>の3つが大切なポイントです。ちょっとした準備と記録が、トラブルのない円満な退去につながります。
                </Typography>
                <Paper sx={{ p: 2, bgcolor: '#f1f8e9', borderLeft: '4px solid #388e3c' }} elevation={0}>
                    <Typography variant="body2">
                        <b>退去時チェックリスト</b>を活用し、ルームメイト同士の連携も大切に。<br />
                        ネガティブな思い出にならないよう、計画的な準備で新生活を気持ちよく迎えましょう！
                    </Typography>
                </Paper>
                <Box sx={{ my: 3 }}>
                    
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-taikyo-checklist',
    slug: 'roomshare-taikyo-checklist',
    title: 'ルームシェアの「退去時のチェックリスト」',
    excerpt: '退去準備のポイント 原状回復の注意点 敷金返還のコツ チェックリスト',
    coverImage: '/images/blog/cover-roomshare-taikyo_roomshare-taikyo-checklist.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく退去準備について話し合い、協力する様子が描かれている。温かい雰囲気と新しい門出への期待感が伝わる構図',
    publishedAt: '2024-06-24T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '退去・引越し', '住まいの知識'],
    tags: ['退去', '原状回復', '敷金', 'ルームシェア', 'チェックリスト'],
    author: {
        name: '伊藤 大輔',
        avatar: '/images/avatars/ito-daisuke.jpg',
        bio: 'シェアハウスやルームシェアの実践的ノウハウを多数発信。住まい選びと快適な共同生活の両立をサポートするライフスタイルアドバイザー。',
        profilePicture: '/images/avatars/ito-daisuke.jpg'
    },
    viewCount: 1240,
    contentComponent: PostContent
};

export default post;